import { skyEquipment } from './77-sky-upgrades.mjs';
import { skyCannonInterval } from './82-sky-bonuses.mjs';
import { skyShotImpact, chargeSkyOverdrive } from './79-sky-precision.mjs';
import {
  skyAimSolution,
  skyTargetFront,
  skyProjectileContact,
  skyDirectAimActive,
} from './80-sky-aim.mjs';
import { skyWeaponMuzzle } from './90-sky-weapon-muzzles.mjs';
import {
  skyFlightFrame,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
  skyFlightVectorToLocal,
  skyFlightVectorToWorld,
} from './92-sky-flight-frame.mjs';

/** Exact escort muzzle coordinates in metres. Renderers use these same slots;
 * active-time sway never changes during pause. Invalid equipment resolves empty. */
export function skyDronePositions(state) {
  return Array.from({ length: skyEquipment(state.upgrades).droneCount }, (_, index) => {
    const row = Math.floor(index / 2),
      side = index % 2 ? 1 : -1;
    const offset = {
      x: side * (1.35 + row * 0.5),
      y: 0.35 + row * 0.18 + Math.sin(state.time * 2 + index) * 0.08,
      z: row * 0.3,
    };
    const rotated = skyFlightVectorToWorld(state.flightFrame, offset);
    return rotated
      ? {
          index,
          x: state.x + rotated.x,
          y: state.y + rotated.y,
          z: (state.pilotZ ?? 1) + rotated.z,
        }
      : { index, x: state.x + offset.x, y: state.y + offset.y, z: 1 + offset.z };
  });
}
/** Conservative simultaneous-bolt budget derived from lifetime and cadence,
 * including overdrive and all eight escorts; no arbitrary count truncation. */
export function skyProjectileLimit(equipment) {
  return (
    equipment.shotCount *
      (Math.ceil(130 / equipment.shotSpeed / (equipment.shotInterval * 0.5)) + 1) +
    equipment.droneCount * (Math.ceil(132 / equipment.droneSpeed / equipment.droneInterval) + 1)
  );
}
/** Remaining ballistic energy after travelled metres, independent of frame rate,
 * camera/player movement or weapon tier. Full power within20m, smoothly reduced
 * to55% at100m; invalid distances safely resolve to muzzle power. */
export function skyProjectileEnergy(distance) {
  const progress = Math.max(0, Math.min(1, ((Number.isFinite(distance) ? distance : 0) - 20) / 80));
  return Math.max(0.55, 1 - 0.45 * progress * progress * (3 - 2 * progress));
}
function bolt(state, origin, aim, damage, source) {
  const framed = !!skyFlightFrame(state.flightFrame);
  const shot = {
    id: state.nextId++,
    kind: 'bolt',
    x: origin.x,
    y: origin.y,
    z: origin.z,
    vx: aim.vx,
    vy: aim.vy,
    vz: aim.vz,
    speed: framed ? Math.hypot(aim.vx, aim.vy, aim.vz) : -aim.vz,
    ...(framed ? { framed: true } : {}),
    damage,
    distance: 0,
    source,
    weaponLevel: state.upgrades.weapon,
    pierceRemaining: 0,
    hitIds: [],
    origin: { x: origin.x, y: origin.y, z: origin.z },
    originSource: origin.source ?? 'canonical',
  };
  state.shots.push(shot);
  return shot;
}
function droneTarget(state, origin) {
  const frame = skyFlightFrame(state.flightFrame);
  if (frame) {
    const localOrigin = skyFlightPointToLocal(frame, origin);
    return [
      ...state.enemies,
      ...(state.boss?.authoredProps ?? []),
      ...(state.boss ? [state.boss] : []),
    ]
      .filter(
        (target) =>
          target.hp > 0 &&
          target.collisionEnabled !== false &&
          !target.defeated &&
          target.kind !== 'pillar' &&
          target.vulnerable !== false,
      )
      .map((target) => ({
        target,
        point: skyFlightPointToLocal(
          frame,
          target.kind === 'boss' ? (target.weakpoint ?? target) : target,
        ),
      }))
      .filter(
        ({ point }) =>
          point &&
          localOrigin.z - point.z > 3 &&
          localOrigin.z - point.z < 80 &&
          Math.hypot(point.x - localOrigin.x, point.y - localOrigin.y) < 9,
      )
      .sort((a, b) => b.point.z - a.point.z || a.target.id - b.target.id)[0]?.target;
  }
  return [
    ...state.enemies,
    ...(state.boss?.authoredProps ?? []),
    ...(state.boss ? [state.boss] : []),
  ]
    .filter(
      (target) =>
        target.hp > 0 &&
        target.collisionEnabled !== false &&
        !target.defeated &&
        target.kind !== 'pillar' &&
        (target.kind !== 'boss' || target.vulnerable) &&
        target.z < -3 &&
        target.z > -80 &&
        Math.hypot(target.x - origin.x, target.y - origin.y) < 9,
    )
    .sort((a, b) => b.z - a.z || a.id - b.id)[0];
}
/** Emit fixed ballistic player/escort bolts. Effective equipment is injected so
 * temporary bonuses and the sight share one policy. Drones do not track in flight. */
export function emitSkyShots(state, equipment, dt, weaponMuzzles) {
  state.shot -= dt;
  if (state.shot <= 0) {
    state.shot += skyCannonInterval(state, equipment);
    const aim = skyAimSolution(
      state,
      equipment,
      skyDirectAimActive(state)
        ? skyWeaponMuzzle(state, weaponMuzzles, 0, equipment.shotCount)
        : undefined,
    );
    for (let i = 0; i < equipment.shotCount; i++) {
      const origin = skyWeaponMuzzle(state, weaponMuzzles, i, equipment.shotCount);
      // Both physical barrels converge on the sight's canonical contact. Each
      // bolt still traverses its own ray, so nearer armor/obstacles can intercept
      // one barrel without granting a pass-through or independently assisted aim.
      const frame = skyFlightFrame(state.flightFrame);
      const localOrigin = frame ? skyFlightPointToLocal(frame, origin) : origin;
      const localAim = frame ? skyFlightPointToLocal(frame, aim) : aim;
      const flight = (localOrigin.z - localAim.z) / equipment.shotSpeed;
      const ahead = localOrigin.z - localAim.z > 0.05;
      const forward = frame
        ? skyFlightVectorToWorld(frame, { x: 0, y: 0, z: -equipment.shotSpeed })
        : { x: 0, y: 0, z: -equipment.shotSpeed };
      const trajectory = {
        ...aim,
        // A stale but valid physical sample can already be beyond a very close
        // contact. Keep firing forward instead of reversing or dividing by zero.
        vx: ahead ? (aim.x - origin.x) / flight : forward.x,
        vy: ahead ? (aim.y - origin.y) / flight : forward.y,
        vz: frame ? (ahead ? (aim.z - origin.z) / flight : forward.z) : aim.vz,
      };
      const shot = bolt(
        state,
        origin,
        trajectory,
        equipment.shotDamage * (equipment.shotCount === 2 ? equipment.dualDamageScale : 1),
        'player',
      );
      shot.aimConfidence = ahead ? aim.confidence : 0;
      shot.barrel = i;
      shot.pierceRemaining = state.overdriveTime > 0 ? equipment.shotPierce : 0;
      state.shotsFired++;
    }
  }
  state.drones ??= [];
  for (const origin of skyDronePositions(state)) {
    const drone = (state.drones[origin.index] ??= {
      index: origin.index,
      cooldown: (origin.index * equipment.droneInterval) / 8,
      lastShotAt: -10,
      targetId: null,
    });
    drone.cooldown -= dt;
    if (drone.cooldown > 0) continue;
    const target = droneTarget(state, origin);
    if (!target) {
      drone.cooldown = 0;
      drone.targetId = null;
      continue;
    }
    const point = target.kind === 'boss' ? (target.weakpoint ?? target) : target;
    let aimZ =
      target.kind === 'boss' ? (point.z ?? skyTargetFront(target)) : skyTargetFront(target);
    const frame = skyFlightFrame(state.flightFrame);
    const localOrigin = frame ? skyFlightPointToLocal(frame, origin) : origin;
    const localPoint = frame ? skyFlightPointToLocal(frame, point) : { ...point, z: aimZ };
    const flight = (localOrigin.z - localPoint.z) / equipment.droneSpeed;
    const lead = (velocity) => Math.max(-1.5, Math.min(1.5, (velocity ?? 0) * flight));
    const velocity = frame
      ? skyFlightVectorToLocal(frame, { x: target.vx ?? 0, y: target.vy ?? 0, z: target.vz ?? 0 })
      : { x: target.vx, y: target.vy };
    const aimed = frame
      ? skyFlightPointToWorld(frame, {
          x: localPoint.x + lead(velocity.x),
          y: localPoint.y + lead(velocity.y),
          z: localPoint.z,
        })
      : { x: point.x + lead(target.vx), y: point.y + lead(target.vy), z: aimZ };
    const aimX = aimed.x,
      aimY = aimed.y;
    aimZ = aimed.z;
    const aim = {
      vx: (aimX - origin.x) / flight,
      vy: (aimY - origin.y) / flight,
      vz: frame ? (aimZ - origin.z) / flight : -equipment.droneSpeed,
    };
    const shot = bolt(state, origin, aim, equipment.droneDamage, 'drone');
    shot.droneIndex = origin.index;
    Object.assign(drone, {
      cooldown: equipment.droneInterval,
      lastShotAt: state.time,
      targetId: target.id,
      aimX,
      aimY,
      aimZ,
    });
    state.droneShotsFired = (state.droneShotsFired ?? 0) + 1;
  }
}

/** Retain only live bolts. Framed flight expires at130 travelled metres in any
 * world direction; legacy flight keeps its established world-Z cutoff. */
export function skyProjectileAlive(shot) {
  return (
    !shot.spent &&
    (shot.framed ? Number.isFinite(shot.distance) && shot.distance < 130 : shot.z > -130)
  );
}

/** Resolve swept physical contacts nearest first. Optional resolveContact is
 * called only after intersection: (state,target,shot,worldContact,{damage,energy,
 * autonomous}). A handled result owns HP/destruction and returns actual removed
 * damage/quality; this module records metrics/art but no generic score/charge.
 * Autonomous damage cannot contribute accuracy or precision rewards. */
export function stepSkyProjectiles(s, dt, { destroy, burst, resolveContact }) {
  // Resolve the nearest collision surface first, independent of spawn order.
  const targets = [...s.enemies, ...(s.boss?.authoredProps ?? []), ...(s.boss ? [s.boss] : [])]
    .filter((target) => target.collisionEnabled !== false)
    .sort((a, b) => skyTargetFront(b) - skyTargetFront(a) || a.id - b.id);
  for (const shot of s.shots) {
    if (shot.spent) continue;
    const speed = Math.hypot(shot.vx ?? 0, shot.vy ?? 0, shot.vz ?? -(shot.speed ?? 120));
    const remaining = shot.framed ? Math.max(0, 130 - (shot.distance ?? 0)) : Infinity;
    if (remaining <= 0) {
      shot.spent = true;
      continue;
    }
    const elapsed = shot.framed && speed > 0 ? Math.min(dt, remaining / speed) : dt;
    const oldX = shot.x,
      oldY = shot.y,
      oldZ = shot.z;
    shot.x += elapsed * (shot.vx ?? 0);
    shot.y += elapsed * (shot.vy ?? 0);
    shot.z += elapsed * (shot.vz ?? -(shot.speed ?? 120));
    const previousDistance = shot.distance ?? 0;
    shot.distance = previousDistance + Math.hypot(shot.x - oldX, shot.y - oldY, shot.z - oldZ);
    const from = { x: oldX, y: oldY, z: oldZ };
    const contacts = targets
      .filter((target) => target.hp > 0 && !target.defeated && !shot.hitIds?.includes(target.id))
      .map((target) => ({
        target,
        contact: skyProjectileContact(target, from, shot, target.contactFrame ?? s.flightFrame),
      }))
      .filter((value) => value.contact)
      .sort(
        (a, b) =>
          Math.hypot(a.contact.x - oldX, a.contact.y - oldY, a.contact.z - oldZ) -
            Math.hypot(b.contact.x - oldX, b.contact.y - oldY, b.contact.z - oldZ) ||
          a.target.id - b.target.id,
      );
    for (const { target, contact } of contacts) {
      const energy = skyProjectileEnergy(
        previousDistance + Math.hypot(contact.x - oldX, contact.y - oldY, contact.z - oldZ),
      );
      const autonomous = shot.source === 'drone';
      const rawDamage =
        (shot.damage ?? 1) *
        energy *
        (autonomous && (target.kind === 'boss' || target.leviathanId != null) ? 0.1 : 1);
      const resolved = resolveContact?.(s, target, shot, contact, {
        damage: rawDamage,
        energy,
        autonomous,
      });
      const handled = resolved?.handled === true;
      const impact = handled
        ? {
            quality: ['armor', 'critical', 'body'].includes(resolved.quality)
              ? resolved.quality
              : 'armor',
          }
        : skyShotImpact(target, contact, !autonomous && target.kind !== 'bonus');
      const damage = handled
        ? Number.isFinite(resolved.damage)
          ? Math.max(0, resolved.damage)
          : 0
        : Math.min(
            target.hp,
            (shot.damage ?? 1) *
              energy *
              impact.multiplier *
              // Close pursuit gives escorts much more time on an exposed core.
              // Their10% contribution supports manual aiming without replacing it.
              (autonomous && target.kind === 'boss' ? 0.1 : 1),
          );
      target.impactAt = target.age;
      target.impactQuality = impact.quality;
      s.lastImpact = {
        id: s.nextId++,
        x: contact.x,
        y: contact.y,
        z: contact.z,
        time: s.time,
        quality: impact.quality,
        damage,
        energy,
        scoreAward: !handled && damage > 0 && impact.quality === 'critical' ? 40 : 0,
      };
      burst(
        s,
        s.lastImpact,
        impact.quality === 'armor' ? 'red' : impact.quality === 'critical' ? 'gold' : 'blue',
      );
      if (!autonomous && impact.quality === 'armor') s.armorHits++;
      if (damage > 0) {
        if (!autonomous && !shot.hitConfirmed) s.hitsLanded++;
        if (autonomous) target.droneAssisted = true;
        shot.hitConfirmed = true;
        if (!autonomous && impact.quality === 'critical') {
          s.criticalHits++;
          if (!handled) {
            s.score += 40;
            chargeSkyOverdrive(s);
          }
        }
        if (handled) {
          if (resolved.disabled) target.defeated = true;
        } else if (target.kind !== 'boss' && target.hp <= damage)
          destroy(s, target, autonomous ? 'drone' : 'shot');
        else target.hp = Math.max(0, target.hp - damage);
      }
      shot.hitIds ??= [];
      shot.hitIds.push(target.id);
      if (
        damage > 0 &&
        !['boss', 'pillar'].includes(target.kind) &&
        impact.quality !== 'armor' &&
        (shot.pierceRemaining ?? 0) > 0
      ) {
        shot.pierceRemaining--;
      } else {
        shot.spent = true;
        if (!shot.framed) shot.z = -200;
        break;
      }
    }
    if (shot.framed && shot.distance >= 130 - 1e-9) shot.spent = true;
  }
}
